import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { formatIntNumber } from '@anytag/shared-utils';
import { ColumnDef } from '@tanstack/react-table';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { YoutubeAnalyticsTrendPayload } from '@anytag/graphql';
import { useGenerateInfluencerProfileLink, usePostDetailsParam } from '@anytag/shared-hooks';
import { MediaPreview, TextCutter, TableCell } from '@anytag/shared-atoms';
import { TrendingPostsTabType } from '../../TrendingPostsFilter/schemaTypes';
import { useTrendingPostsFilter } from '../../TrendingPostsFilter';

interface Props {
  tab: TrendingPostsTabType;
}

export const useTrendingPostsTable = ({ tab }: Props) => {
  const { t } = useTranslation();
  const { page, limit } = useTrendingPostsFilter();
  const { setPostDetailsParam } = usePostDetailsParam();
  const { isAdvertiser, isTalentAgency } = useUserRoles();
  const { generateProfileLink } = useGenerateInfluencerProfileLink({ disabled: isAdvertiser || isTalentAgency });

  const columns: ColumnDef<YoutubeAnalyticsTrendPayload>[] = [
    {
      accessorKey: 'ranking',
      header: t<string>('HeaderColumn.Ranking'),
      meta: { align: 'center' },
      cell: ({ row }) => (
        <TableCell.Text
          className="min-w-15 pr-2 text-center"
          value={String(page && limit ? (page - 1) * limit + row.index + 1 : 0)}
        />
      )
    },
    {
      accessorKey: 'post',
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => (
        <div className="flex max-w-125 items-center py-2">
          <MediaPreview
            style={{ ['--size' as string]: '80px' }}
            src={row.original.videoThumbnail}
            className="size-[var(--size)] rounded-[5px]"
            onClick={() => setPostDetailsParam(row.original.id)}
          />
          <div className="ml-4">
            <TableCell.Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setPostDetailsParam(row.original.id);
              }}
              label={row.original.videoTitle}
            />
            <TextCutter text={row.original.videoCaption} lines={4} className="text-sm text-tertiary" />
          </div>
        </div>
      )
    },
    {
      accessorKey: 'channel',
      header: t<string>('HeaderColumn.Channel'),
      cell: ({ row }) => {
        const { influencerId, socialAccountId, channelAvatar, channel } = row.original;

        return (
          <TableCell.Link
            className="flex max-w-50 items-center"
            {...generateProfileLink(
              influencerId,
              socialAccountId ? { filter: { id: String(socialAccountId), sm: tab } } : undefined
            )}
          >
            <InfluencerAvatar size="32px" src={channelAvatar} className="rounded-full" asChild />
            <TableCell.Text value={channel} className="ml-2" />
          </TableCell.Link>
        );
      }
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number className="min-w-25" value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'comments',
      header: t<string>('HeaderColumn.Comments'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number className="min-w-25" value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      cell: ({ row }) => <TableCell.Date value={row.original.postDate} />
    }
  ];

  return { columns };
};
